import { useEffect, useState } from "react";
import { toast } from "sonner";
import { partnerAdminApi } from "@/lib/partner";

function money(amt, cur) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: (cur || "USD").toUpperCase() })
      .format((Number(amt) || 0) / 100);
  } catch { return `${amt}`; }
}
function discountLabel(camp) {
  if (!camp) return "—";
  return camp.discount_type === "percent" ? `${camp.percent_off}% off` : `${money(camp.amount_off, camp.currency)} off`;
}
function durationLabel(camp) {
  if (!camp) return "";
  if (camp.duration === "repeating") return `for ${camp.duration_in_months} mo`;
  if (camp.duration === "forever") return "forever";
  return "once";
}

const EMPTY_FORM = {
  name: "",
  notes: "",
  discount_type: "percent",
  percent_off: "20",
  amount_off: "",
  currency: "usd",
  duration: "once",
  duration_in_months: "",
  per_partner_max_codes: "5",
  allowed_all_partners: true,
  code_prefix: "",
};

export default function PartnerCampaignsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [expandCodes, setExpandCodes] = useState(null); // campaign_id
  const [codes, setCodes] = useState({});               // { [campaignId]: rows[] }

  const load = async () => {
    setLoading(true);
    try {
      const r = await partnerAdminApi.listCampaigns();
      setItems(r.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim() || undefined,
        notes: form.notes.trim() || undefined,
        discount_type: form.discount_type,
        duration: form.duration,
        currency: form.currency || "usd",
        allowed_all_partners: !!form.allowed_all_partners,
        code_prefix: form.code_prefix.trim() || undefined,
      };
      if (form.discount_type === "percent") {
        const p = Number(form.percent_off);
        if (!Number.isFinite(p) || p <= 0 || p > 100) throw new Error("Percent off must be between 0 and 100");
        payload.percent_off = p;
      } else {
        const a = Number(form.amount_off);
        if (!Number.isFinite(a) || a <= 0) throw new Error("Amount off must be a positive number");
        payload.amount_off = a;
      }
      if (form.duration === "repeating") {
        const m = Number(form.duration_in_months);
        if (!Number.isFinite(m) || m < 1) throw new Error("Duration in months must be at least 1");
        payload.duration_in_months = Math.floor(m);
      }
      if (form.per_partner_max_codes) {
        const n = Number(form.per_partner_max_codes);
        if (Number.isFinite(n) && n > 0) payload.per_partner_max_codes = Math.floor(n);
      }
      await partnerAdminApi.createCampaign(payload);
      toast.success("Campaign created");
      setOpenCreate(false); setForm(EMPTY_FORM); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 503) {
        toast.error("Stripe is not configured — cannot create Coupon. Add STRIPE_SECRET_KEY and retry.");
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail?.hint) {
        toast.error(detail.hint);
      } else {
        toast.error(e?.message || "Failed to create campaign");
      }
    } finally { setBusy(false); }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this campaign? All child codes will also be disabled on Stripe.")) return;
    try {
      const r = await partnerAdminApi.deactivateCampaign(id);
      toast.success(`Deactivated (${r.deactivated_codes} code${r.deactivated_codes === 1 ? "" : "s"})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail?.detail || e?.response?.data?.detail || "Failed"); }
  };

  const toggleCodes = async (id) => {
    if (expandCodes === id) { setExpandCodes(null); return; }
    setExpandCodes(id);
    if (!codes[id]) {
      try {
        const r = await partnerAdminApi.listCampaignCodes(id);
        setCodes((s) => ({ ...s, [id]: r.items || [] }));
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load codes"); }
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-partner-campaigns">
      <div className="flex items-center justify-between">
        <p className="text-xs text-white/50">
          Discount campaigns that partners can turn into unique tracked codes. Coupons are created on Stripe on save.
        </p>
        <button
          onClick={() => setOpenCreate(true)}
          data-testid="new-campaign-btn"
          className="rounded-lg bg-[#D6A653] px-3 py-1.5 text-xs text-black"
        >
          New campaign
        </button>
      </div>

      {loading ? <p className="text-white/50">Loading…</p> : items.length === 0 ? (
        <p className="text-white/40" data-testid="no-admin-campaigns">No campaigns yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} className="rounded-2xl border border-white/10 bg-[#0B0D12] p-4" data-testid={`admin-campaign-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-white">{c.name}</p>
                  <p className="mt-0.5 text-xs text-white/50">
                    {discountLabel(c)} · {durationLabel(c)} · Coupon <span className="font-mono">{c.stripe_coupon_id}</span>
                  </p>
                  <p className="mt-1 text-[11px] text-white/40">
                    {c.allowed_all_partners ? "All partners" : `${(c.allowed_partner_ids || []).length} partner(s)`} ·
                    Up to {c.per_partner_max_codes} code(s) per partner · {c.active ? <span className="text-emerald-300">Active</span> : <span className="text-white/50">Inactive</span>}
                  </p>
                  {c.notes ? <p className="mt-2 text-xs text-white/50">{c.notes}</p> : null}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleCodes(c.id)}
                    data-testid={`view-codes-${c.id}`}
                    className="rounded-lg border border-white/15 px-2.5 py-1 text-xs text-white/70 hover:bg-white/5"
                  >
                    {expandCodes === c.id ? "Hide codes" : "View codes"}
                  </button>
                  {c.active && (
                    <button
                      onClick={() => deactivate(c.id)}
                      data-testid={`deactivate-campaign-${c.id}`}
                      className="rounded-lg border border-red-500/40 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/10"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </div>

              {expandCodes === c.id && (
                <div className="mt-4 overflow-x-auto rounded-xl border border-white/10 bg-black/30">
                  <table className="min-w-full text-xs">
                    <thead className="text-left uppercase tracking-wider text-white/40">
                      <tr>
                        <th className="px-3 py-2">Code</th>
                        <th className="px-3 py-2">Partner</th>
                        <th className="px-3 py-2 text-right">Redeemed</th>
                        <th className="px-3 py-2 text-right">Max</th>
                        <th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="text-white/80">
                      {(codes[c.id] || []).length === 0 ? (
                        <tr><td className="px-3 py-3 text-white/40" colSpan={5}>No codes generated yet.</td></tr>
                      ) : (codes[c.id] || []).map((cc) => (
                        <tr key={cc.id} className="border-t border-white/5">
                          <td className="px-3 py-2 font-mono text-[#D6A653]">{cc.code}</td>
                          <td className="px-3 py-2 text-white/70">{cc.partner?.partner_code || cc.partner_id}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{cc.times_redeemed_cached ?? 0}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-white/50">{cc.max_redemptions ?? "∞"}</td>
                          <td className="px-3 py-2">{cc.active ? <span className="text-emerald-300">active</span> : <span className="text-white/50">inactive</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {openCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="new-campaign-modal">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0B0D12] p-6">
            <h3 className="text-lg font-light text-white">New partner campaign</h3>
            <p className="mt-1 text-xs text-white/50">Creates a Stripe Coupon. The coupon is immutable — deactivate the campaign to disable it.</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="camp-name" />
              <Field label="Code prefix (cosmetic)" value={form.code_prefix} onChange={(v) => setForm({ ...form, code_prefix: v })} testId="camp-prefix" />
              <div>
                <Label>Discount type</Label>
                <select
                  value={form.discount_type}
                  onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                  data-testid="camp-discount-type"
                  className="mt-1 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white"
                >
                  <option value="percent">Percent off</option>
                  <option value="amount">Amount off</option>
                </select>
              </div>
              {form.discount_type === "percent" ? (
                <Field label="Percent off" value={form.percent_off} onChange={(v) => setForm({ ...form, percent_off: v })} testId="camp-percent" />
              ) : (
                <>
                  <Field label={`Amount off (${form.currency.toUpperCase()})`} value={form.amount_off} onChange={(v) => setForm({ ...form, amount_off: v })} testId="camp-amount" />
                  <Field label="Currency (ISO)" value={form.currency} onChange={(v) => setForm({ ...form, currency: v.toLowerCase() })} testId="camp-currency" />
                </>
              )}
              <div>
                <Label>Duration</Label>
                <select
                  value={form.duration}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  data-testid="camp-duration"
                  className="mt-1 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white"
                >
                  <option value="once">Once</option>
                  <option value="repeating">Repeating</option>
                  <option value="forever">Forever</option>
                </select>
              </div>
              {form.duration === "repeating" && (
                <Field label="Duration in months" value={form.duration_in_months} onChange={(v) => setForm({ ...form, duration_in_months: v })} testId="camp-duration-months" />
              )}
              <Field label="Max codes per partner" value={form.per_partner_max_codes} onChange={(v) => setForm({ ...form, per_partner_max_codes: v })} testId="camp-per-partner" />
              <label className="col-span-full flex items-center gap-2 text-xs text-white/70">
                <input
                  type="checkbox"
                  checked={form.allowed_all_partners}
                  onChange={(e) => setForm({ ...form, allowed_all_partners: e.target.checked })}
                  data-testid="camp-allow-all"
                />
                Available to all active partners
              </label>
              <div className="col-span-full">
                <Label>Notes (internal)</Label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  data-testid="camp-notes"
                  className="mt-1 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => { setOpenCreate(false); setForm(EMPTY_FORM); }}
                data-testid="camp-cancel"
                className="rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70"
              >
                Cancel
              </button>
              <button
                onClick={create}
                disabled={busy}
                data-testid="camp-submit"
                className="rounded-lg bg-[#D6A653] px-4 py-2 text-sm text-black disabled:opacity-60"
              >
                {busy ? "Creating…" : "Create campaign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Label = ({ children }) => <span className="block text-[11px] uppercase tracking-wider text-white/50">{children}</span>;
const Field = ({ label, value, onChange, testId }) => (
  <label className="block">
    <Label>{label}</Label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="mt-1 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-[#D6A653]/50"
    />
  </label>
);
